import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { Send, Loader2, CheckCircle2, AlertCircle, Search, X, Users, User } from 'lucide-react'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000'

interface Recipient {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
}

interface EmailComposerProps {
  allowedRecipientRoles: ('student' | 'teacher' | 'admin')[]
  senderRole: 'admin' | 'teacher'
}

export default function EmailComposer({ allowedRecipientRoles, senderRole }: EmailComposerProps) {
  const { profile } = useAuth()
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([])
  const [search, setSearch] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadRecipients()
  }, [])

  const loadRecipients = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('profiles')
      .select('id, email, first_name, last_name, role')
      .in('role', allowedRecipientRoles)
      .order('first_name')

    if (data) setRecipients(data as Recipient[])
    setLoading(false)
  }

  const filteredRecipients = recipients.filter((r) => {
    if (selectedRecipients.find((s) => s.id === r.id)) return false
    if (!search) return true
    const q = search.toLowerCase()
    return (
      r.first_name.toLowerCase().includes(q) ||
      r.last_name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q)
    )
  })

  const addRecipient = (r: Recipient) => {
    setSelectedRecipients((prev) => [...prev, r])
    setSearch('')
  }

  const removeRecipient = (id: string) => {
    setSelectedRecipients((prev) => prev.filter((r) => r.id !== id))
  }

  const selectAll = () => {
    setSelectedRecipients(recipients)
    setSearch('')
  }

  const handleSend = async () => {
    if (selectedRecipients.length === 0 || !subject.trim() || !message.trim()) return

    setSending(true)
    setStatus('idle')
    setErrorMsg('')

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('No hay sesión activa')

      const res = await fetch(`${BACKEND_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          to: selectedRecipients.map((r) => r.email),
          subject,
          message,
          senderRole: profile?.role || senderRole,
          senderName: profile ? `${profile.first_name} ${profile.last_name}`.trim() : undefined,
        }),
      })

      const data = await res.json()

      if (!res.ok) throw new Error(data.error || 'Error al enviar')

      setStatus('success')
      setSubject('')
      setMessage('')
      setSelectedRecipients([])
    } catch (err: any) {
      setStatus('error')
      setErrorMsg(err.message)
    } finally {
      setSending(false)
    }
  }

  const roleLabel = (role: string) => {
    switch (role) {
      case 'student': return 'Alumno'
      case 'teacher': return 'Profesor'
      case 'admin': return 'Admin'
      default: return role
    }
  }

  const roleColor = (role: string) => {
    switch (role) {
      case 'student': return 'bg-blue-100 text-blue-700'
      case 'teacher': return 'bg-purple-100 text-purple-700'
      case 'admin': return 'bg-red-100 text-red-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const isAdmin = senderRole === 'admin'
  const accentColor = isAdmin ? '#0055A4' : 'rgb(99 102 241)'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Enviar Correo</h1>
        <p className="text-gray-600">
          Envía un correo electrónico desde cfi.nomadlexis@gmail.com
        </p>
      </div>

      {/* Status messages */}
      {status === 'success' && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
          <p className="text-green-800 text-sm">Correo enviado exitosamente.</p>
        </div>
      )}

      {status === 'error' && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
          <p className="text-red-800 text-sm">Error: {errorMsg}</p>
        </div>
      )}

      <div className="bg-white rounded-xl border shadow-sm">
        {/* Destinatarios */}
        <div className="p-4 border-b">
          <label htmlFor="search-recipients" className="block text-sm font-medium text-gray-700 mb-2">
            Destinatarios
          </label>

          {/* Selected chips */}
          {selectedRecipients.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedRecipients.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-sm"
                >
                  <User className="w-3 h-3 text-gray-500" />
                  {r.first_name} {r.last_name}
                  <button
                    onClick={() => removeRecipient(r.id)}
                    className="ml-0.5 p-0.5 hover:bg-gray-200 rounded-full"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input id="search-recipients" type="text" value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre o email..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': accentColor } as any}
              />
            </div>
            <button
              onClick={selectAll}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Users className="w-4 h-4" />
              Todos
            </button>
          </div>

          {/* Dropdown list */}
          {search && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y">
              {loading ? (
                <div className="p-3 text-center text-sm text-gray-500">Cargando...</div>
              ) : filteredRecipients.length === 0 ? (
                <div className="p-3 text-center text-sm text-gray-500">Sin resultados</div>
              ) : (
                filteredRecipients.slice(0, 20).map((r) => (
                  <button
                    key={r.id}
                    onClick={() => addRecipient(r)}
                    className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {r.first_name} {r.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{r.email}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleColor(r.role)}`}>
                      {roleLabel(r.role)}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}

          <p className="mt-2 text-xs text-gray-500">
            {selectedRecipients.length} destinatario{selectedRecipients.length !== 1 ? 's' : ''} seleccionado{selectedRecipients.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Asunto */}
        <div className="p-4 border-b">
          <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700 mb-2">
            Asunto
          </label>
          <input id="email-subject" type="text" value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Asunto del correo..."
            className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ '--tw-ring-color': accentColor } as any}
          />
        </div>

        {/* Mensaje */}
        <div className="p-4 border-b">
          <label htmlFor="email-message" className="block text-sm font-medium text-gray-700 mb-2">
            Mensaje
          </label>
          <textarea id="email-message" value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Escribe tu mensaje aquí..."
            rows={10}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-y"
            style={{ '--tw-ring-color': accentColor } as any}
          />
          <p className="mt-1 text-xs text-gray-500">
            El correo se enviara con formato y branding de NomadLexis.
          </p>
        </div>

        {/* Enviar */}
        <div className="p-4 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Se enviara desde: cfi.nomadlexis@gmail.com
          </p>
          <button
            onClick={handleSend}
            disabled={sending || selectedRecipients.length === 0 || !subject.trim() || !message.trim()}
            className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: accentColor }}
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Enviar Correo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
